// Sérialisations canoniques et STABLES de l'ENTRÉE d'un résultat IA (ce que l'utilisateur a soumis),
// PAS la sortie de Claude (non déterministe). Base du hash SHA-256 `result_ref` :
//   - régénérer/re-noter avec la MÊME entrée => même result_ref => le feedback s'upserte ;
//   - une entrée différente => nouveau result_ref => nouveau feedback possible.
// Ordre figé (tableaux positionnels, aucune clé d'objet ambiguë, aucun timestamp) → deux entrées
// identiques produisent le même hash. JAMAIS stockées : seul leur hash SHA-256 part en base.

export interface CvScoreInputRef {
  pdf?: { data: string };
  docx?: { data: string };
  text?: string;
  offer?: unknown;
  targetRole?: string;
}

// Entrée de la notation : le document (base64 PDF/Word ou texte) + l'offre + le poste visé.
export function cvScoreInputKey(input: CvScoreInputRef): string {
  return JSON.stringify([
    'cv_score',
    input.pdf?.data ?? null,
    input.docx?.data ?? null,
    input.text ?? null,
    input.offer ?? null,
    input.targetRole ?? null,
  ]);
}

export interface LinkedinInputRef {
  images?: { media_type: string; data: string }[];
  profile?: string;
}

// Entrée de l'audit : les captures (base64) et/ou le texte collé du profil.
export function linkedinAuditInputKey(input: LinkedinInputRef): string {
  return JSON.stringify(['linkedin_audit', input.images ?? null, input.profile ?? null]);
}

// Entrée de la création : les réponses du wizard (buildLabeled), tableau ordonné par QUESTIONS.
// Mêmes réponses => même clé, même si Claude régénère un CV différent.
export function cvCreateAnswersKey(answers: string[]): string {
  return JSON.stringify(['cv_create', 'answers', answers]);
}

export interface CoverLetterInputRef {
  cvId: string;
  annonce: string;
  profil: string;
  typeCandidature: string;
  ton: string;
  langue: string;
  destinataireNom: string;
  destinataireFonction: string;
  organisation: string;
  metier: string;
  ville: string;
  emailAccompagnement: boolean;
}

// Entrée de la génération : le CV choisi (par id, stable) + l'annonce + les paramètres du
// formulaire, à plat et dans un ordre figé — regénérer avec les mêmes réglages upserte l'avis,
// changer un seul champ (le ton, par exemple) ouvre un nouvel avis.
export function coverLetterInputKey(input: CoverLetterInputRef): string {
  return JSON.stringify([
    'cover_letter',
    input.cvId,
    input.annonce,
    input.profil,
    input.typeCandidature,
    input.ton,
    input.langue,
    input.destinataireNom,
    input.destinataireFonction,
    input.organisation,
    input.metier,
    input.ville,
    input.emailAccompagnement,
  ]);
}

// Lettre rouverte depuis « Mes lettres » : le formulaire n'a pas été rejoué, l'identité stable
// est son id de sauvegarde. Même logique que cvCreateSavedKey.
export function coverLetterSavedKey(letterId: string): string {
  return JSON.stringify(['cover_letter', 'saved', letterId]);
}

// CV rouvert (aucun wizard rejoué) : identité stable = son id de sauvegarde.
export function cvCreateSavedKey(cvId: string): string {
  return JSON.stringify(['cv_create', 'saved', cvId]);
}
